import { Capacitor } from '@capacitor/core';
import { getFeaturedItemAndTextForTracking, useGA4 } from '@travelwin/core';
import { useContext } from 'react';
import { v4 as uuidv4 } from 'uuid';

import MetaMobileAnalytics, {
  AddToCartEventData,
  ANALYTICS_EVENT_TYPES,
  getFromText,
  useAnalytics,
} from '../analytics';
import { useNavigate } from '../components/Link';
import { useMetaPixelTracker } from '../components/MetaPixel';
import { useTikTokPixelTracker } from '../components/TikTokPixel';
import { EuiccManagerContext } from '../context/EuiccManager';
import { useUser } from '../context/User';
import { ProductCheckoutContext } from '@/pages/PlanSelection/components';

export const useBuyPlanHandler = () => {
  const analyticsContext = useAnalytics();
  const { user } = useUser();
  const { trackMetaPixel } = useMetaPixelTracker();
  const { trackTikTokPixel } = useTikTokPixelTracker();
  const { trackEvent } = useGA4();
  const { isEuiccManagerEnabled: isDeviceEsimEnabled } =
    useContext(EuiccManagerContext);
  const navigate = useNavigate();

  return ({
    product,
    countryName = 'n/a',
    checkoutButtonType = 'original',
    itemIndex,
    actionViaSource,
    baseItemId,
  }: ProductCheckoutContext) => {
    const trackingData: AddToCartEventData = {
      checkoutButtonType,
      currency:
        product.product_offering_pricing[0].currency.alias.toUpperCase(),
      price: product.product_offering_pricing[0].total_price_gross,
      items: [
        {
          index: itemIndex,
          id: product.id,
          name: product.name,
          brand: product.mvno.alias,
          quantity: 1,
          retailerId: product.retailer.id,
          countryName,
          itemListName: actionViaSource ? getFromText(actionViaSource) : 'n/a',
          price: product.product_offering_pricing[0].total_price_gross,
          currency:
            product.product_offering_pricing[0].currency.alias.toUpperCase(),
          baseItemId,
          ...getFeaturedItemAndTextForTracking(product),
        },
      ],
    };

    analyticsContext?.trackAddToCart(trackingData);

    trackEvent(ANALYTICS_EVENT_TYPES.PlanSelectionPageBuyClicked, {
      currency: product.product_offering_pricing[0].currency.alias,
      country: countryName,
      productName: product.name,
      mvnoName: product.mvno.alias,
      value: product.product_offering_pricing[0].total_price_gross,
    });

    trackMetaPixel({
      event_id: uuidv4(),
      event_name: 'AddToCart',
      email: user?.email?.trim().toLowerCase(),
      value: product.product_offering_pricing[0].total_price_gross,
      currency:
        product.product_offering_pricing[0].currency.alias.toUpperCase(),
      content_ids: [product.id],
      content_name: product.name,
      content_type: 'product',
    });

    trackTikTokPixel(
      {
        event_id: uuidv4(),
        event_name: 'AddToCart',
        email: user?.email?.trim().toLowerCase(),
        price: product.product_offering_pricing[0].total_price_gross,
        currency:
          product.product_offering_pricing[0].currency.alias.toUpperCase(),
        content_id: product.id,
        content_name: product.name,
        content_type: 'product',
      },
      true,
    );

    MetaMobileAnalytics.sendAddedToCartEvent({
      amount: product.product_offering_pricing[0].total_price_gross,
      currency:
        product.product_offering_pricing[0].currency.alias.toUpperCase(),
      contentID: product.id,
      contentType: 'product',
    });

    const analyticsData = {
      countryName,
      itemIndex,
      actionViaSource,
      baseItemId,
    };

    if (!Capacitor.isNativePlatform() || isDeviceEsimEnabled) {
      navigate(`/order-summary/${product.id}`, {
        state: {
          analyticsData,
        },
      });
    } else {
      navigate(`/esim-compatibility/${product.id}`, {
        state: {
          analyticsData,
        },
      });
    }
  };
};
